"use strict";
var __importDefault = (this && this.__importDefault) || function (mod) {
    return (mod && mod.__esModule) ? mod : { "default": mod };
};
Object.defineProperty(exports, "__esModule", { value: true });
exports.getAllBalancesByAccountIdEx = exports.getAllBalancesByAccountId = exports.getBalancesForAccountsAtDates = exports.getBalanceForAccountAtDate = exports.getBalancesByAccountId = exports.getBalanceContributingTxnCount = exports.includeRecurringTxn = exports.isConnectedAccount = void 0;
const reselect_1 = require("reselect");
const immutable_1 = require("immutable");
const luxon_1 = require("luxon");
const store_1 = __importDefault(require("store"));
const core_1 = require("companion-app-components/utils/core");
const accounts_1 = require("companion-app-components/flux/accounts");
const types_1 = require("companion-app-components/flux/preferences/types");
const selectors_1 = require("companion-app-components/flux/preferences/selectors");
const selectors_2 = require("companion-app-components/flux/investmentHoldings/selectors");
const feature_flags_1 = require("companion-app-components/flux/feature-flags");
const selectors_3 = require("data/preferences/selectors");
const selectors_4 = require("data/transactions/selectors");
const utils_1 = require("data/transactions/utils");
const optimalCacheManager_1 = require("data/optimalCacheManager");
const selectors_5 = require("data/accounts/selectors");
const utils_2 = require("utils/utils");
const memoizeWithKey_1 = require("utils/memoizeWithKey");
const types_2 = require("./types");
const balCache_1 = require("./balCache");
const log = (0, core_1.getLogger)('data/accountBalances/selectors');
function isConnectedAccount(account) {
    return account.institutionLoginId &&
        account.aggregators && account.aggregators.size > 0;
}
exports.isConnectedAccount = isConnectedAccount;
function includeRecurringTxn(txn, recurringSetting, transactionsForAccountId) {
    // "next scheduled instance", but this needs to be the next scheduled instance after TODAY
    // so it still shows all prior instances
    const txnDate = luxon_1.DateTime.fromISO(txn.postedOn);
    const currentDate = luxon_1.DateTime.local();
    const sanitizeNaN = (0, utils_2.noNaN)(recurringSetting);
    if (sanitizeNaN === -1) {
        // include all due and overdue
        const isSameOrBefore = txnDate <= currentDate || txnDate.hasSame(currentDate, 'day');
        if (isSameOrBefore) {
            return true;
        }
        const nextInstanceAfterToday = (0, utils_1.nextScheduledInstanceAfterDate)(currentDate, transactionsForAccountId, txn.stModelId);
        return nextInstanceAfterToday && (txn.id === nextInstanceAfterToday.id);
    }
    if (sanitizeNaN === 0) {
        return false;
    }
    const mostRecentSchedTxnDate = currentDate.plus({ days: sanitizeNaN });
    return txnDate.hasSame(mostRecentSchedTxnDate, 'day') || txnDate <= mostRecentSchedTxnDate;
}
exports.includeRecurringTxn = includeRecurringTxn;
function includeTransactionInBalance(txn, { recurringSetting, transactionsForAccountId, excludePendingTxn, isPendingTxnDisabled, }) {
    if ((isPendingTxnDisabled || excludePendingTxn) && (0, utils_1.isBankOwnedPending)(txn)) {
        return false;
    }
    if ((0, utils_1.isUnacceptedScheduledTxn)(txn)) {
        return includeRecurringTxn(txn, recurringSetting, transactionsForAccountId);
    }
    return !(0, utils_1.isC2RTransaction)(txn);
}
// CMP-12314
// Count only the transactions that actually feed the balance-mismatch calculation.
// getTransactionsByAccountId returns every txn in the account, including ones outside the
// mismatch window and bank pending / out-of-window scheduled (SCHEDULED_TRANSACTION_PENDING)
// txns that are NOT part of the balance used for the mismatch delta.
//
// We reuse includeTransactionInBalance (the same gate getBalancesByAccountId /
// calcBalanceForAccountAtDate use) so bank-owned pending (when pending is excluded), C2R and
// out-of-window scheduled txns are dropped, while user-owned txns and in-window scheduled
// reminders are kept.
//
// The mismatch delta is the difference between the two known balances (balanceAsOf @ balanceAsOfOn
// and currentBalanceAsOf @ currentBalanceAsOfOn), so the txns that drive it are the ones posted
// between those two anchor dates. Either date can be the earlier one (e.g. balanceAsOfOn may be far
// in the past while currentBalanceAsOfOn is ~today), so the window is derived order-independently.
// A txn on the earlier anchor is already reflected in that balance (exclude); a txn on the later
// anchor is part of the later balance (include): (earlier, later].
const getBalanceContributingTxnCount = (account, txnsForAccount, excludePendingTxn) => {
    if (!txnsForAccount || !account) {
        return 0;
    }
    const recurringSetting = accounts_1.accountsUtils.getReminderSettingInDays(account.recurringTxn);
    const currentBalanceAsOfOnDate = account.currentBalanceAsOfOn ? luxon_1.DateTime.fromISO(account.currentBalanceAsOfOn) : null;
    const balanceAsOfOnDate = account.balanceAsOfOn ? luxon_1.DateTime.fromISO(account.balanceAsOfOn) : null;
    let windowStart = null;
    let windowEnd = null;
    if (currentBalanceAsOfOnDate && balanceAsOfOnDate) {
        const currentIsEarlier = currentBalanceAsOfOnDate < balanceAsOfOnDate;
        windowStart = currentIsEarlier ? currentBalanceAsOfOnDate : balanceAsOfOnDate;
        windowEnd = currentIsEarlier ? balanceAsOfOnDate : currentBalanceAsOfOnDate;
    }
    else {
        // Only one anchor is known: use it as the (inclusive) upper bound, no lower bound.
        windowEnd = currentBalanceAsOfOnDate !== null && currentBalanceAsOfOnDate !== void 0 ? currentBalanceAsOfOnDate : balanceAsOfOnDate;
    }
    let count = 0;
    txnsForAccount.forEach((txn) => {
        if (!includeTransactionInBalance(txn, {
            recurringSetting,
            transactionsForAccountId: txnsForAccount,
            excludePendingTxn,
            isPendingTxnDisabled: account.isBankPendingTxnsExcluded,
        })) {
            return;
        }
        // Keep only txns inside the mismatch window: (earlier anchor, later anchor]
        const txnDate = luxon_1.DateTime.fromISO(txn.postedOn);
        if (windowStart && txnDate <= windowStart) {
            return;
        }
        if (windowEnd && txnDate > windowEnd) {
            return;
        }
        count += 1;
    });
    return count;
};
exports.getBalanceContributingTxnCount = getBalanceContributingTxnCount;
function didBalanceCacheBreak(cacheRec, props) {
    var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t;
    const { acctReminderSetting, account, txns } = props;
    return (!cacheRec) ||
        (cacheRec.txns !== txns) ||
        (cacheRec.acctReminderSetting !== acctReminderSetting) ||
        (cacheRec.account.currentBalanceAsOf !== account.currentBalanceAsOf) ||
        (cacheRec.account.onlineBalance !== account.onlineBalance) ||
        (cacheRec.account.balanceAsOf !== account.balanceAsOf) ||
        (cacheRec.account.currentBalanceAsOfOn !== account.currentBalanceAsOfOn) ||
        (cacheRec.account.balanceAsOfOn !== account.balanceAsOfOn) ||
        (((_c = (_b = (_a = cacheRec.account.aggregators) === null || _a === void 0 ? void 0 : _a.first) === null || _b === void 0 ? void 0 : _b.call(_a)) === null || _c === void 0 ? void 0 : _c.onlineBalance) !== ((_f = (_e = (_d = account.aggregators) === null || _d === void 0 ? void 0 : _d.first) === null || _e === void 0 ? void 0 : _e.call(_d)) === null || _f === void 0 ? void 0 : _f.onlineBalance)) ||
        (((_j = (_h = (_g = cacheRec.account.aggregators) === null || _g === void 0 ? void 0 : _g.first) === null || _h === void 0 ? void 0 : _h.call(_g)) === null || _j === void 0 ? void 0 : _j.onlineBalanceAt) !== ((_m = (_l = (_k = account.aggregators) === null || _k === void 0 ? void 0 : _k.first) === null || _l === void 0 ? void 0 : _l.call(_k)) === null || _m === void 0 ? void 0 : _m.onlineBalanceAt)) ||
        (((_q = (_p = (_o = cacheRec.account.aggregators) === null || _o === void 0 ? void 0 : _o.first) === null || _p === void 0 ? void 0 : _p.call(_o)) === null || _q === void 0 ? void 0 : _q.availableBalance) !== ((_t = (_s = (_r = account.aggregators) === null || _r === void 0 ? void 0 : _r.first) === null || _s === void 0 ? void 0 : _s.call(_r)) === null || _t === void 0 ? void 0 : _t.availableBalance));
}
function createNewBalancesRecord(cacheRec, props) {
    const { account, isConnected } = props;
    const endBal = cacheRec ? cacheRec.endingBalance : 0;
    const endBalNoRecurring = cacheRec ? cacheRec.endingBalanceNoRecurring : 0;
    const currBal = cacheRec ? cacheRec.currentBalance : 0;
    const finalBal = cacheRec ? cacheRec.finalBalance : 0;
    const txnsDeltaFromCurrentBalance = cacheRec ? cacheRec.txnsDeltaFromCurrentBalance : 0;
    const eDate = cacheRec ? cacheRec.earliestTransactionDate : null;
    return new types_2.AccountBalances({
        accountId: account.id,
        accountType: account.type,
        endingBalance: endBal,
        endingBalanceNoRecurring: endBalNoRecurring,
        onlineBalance: !isConnected ? currBal : account.onlineBalance,
        currentBalance: currBal,
        finalBalance: finalBal,
        earliestTransactionDate: eDate,
        txnsDeltaFromCurrentBalance,
        cpOnlineBalance: cacheRec === null || cacheRec === void 0 ? void 0 : cacheRec.cpOnlineBalance,
        cpAvailableBalance: cacheRec === null || cacheRec === void 0 ? void 0 : cacheRec.cpAvailableBalance,
    });
}
const balRound = (val) => val && Number(val.toFixed(2));
const optimalCacheCompareFn = (oldTxn, newTxn, accountId) => {
    // if this txn change does not involve this account, then no recalc needed
    if (oldTxn.accountId !== accountId && newTxn.accountId !== accountId) {
        return true;
    }
    // if there is no oldTxn (add), then return false
    if (!oldTxn)
        return false;
    //
    // Need to return false for all cases in which a change in a transaction field can cause a balance to change
    //
    if (newTxn.accountId !== oldTxn.accountId)
        return false;
    if (newTxn.isDeleted)
        return false;
    // did we move forward or backwards relative to today?
    const currentDate = luxon_1.DateTime.local();
    const oldTxnDate = luxon_1.DateTime.fromISO(oldTxn.postedOn);
    const newTxnDate = luxon_1.DateTime.fromISO(newTxn.postedOn);
    if ((oldTxnDate <= currentDate && newTxnDate > currentDate) || (newTxnDate <= currentDate && oldTxnDate > currentDate)) {
        return false;
    }
    return (oldTxn.amount === newTxn.amount && oldTxn.postedOn === newTxn.postedOn);
};
exports.getBalancesByAccountId = (0, reselect_1.createSelector)(accounts_1.accountsSelectors.getAccountsByIdIncludingGoals, (state) => (0, selectors_4.getTransactionsByAccountId)(state, { txnTypes: selectors_4.TxnTypes.REGULAR_TXNS | selectors_4.TxnTypes.UNACCEPTED_SCHEDULED_TXNS }), selectors_4.getIsLoading, selectors_5.getBankingAccountLoadPending, selectors_4.getTransactionsStore, selectors_3.getRunningBalanceExcludeAccountIds, ((function combinerClosure() {
    let overallMapCache;
    return ((accounts, transactionsByAccountId, transactionsAreLoading, accountsAreLoading, transactionsStore, runningBalanceExcludeAccountIds) => {
        let atLeastOneAccountBrokeCache = false;
        const retVal = ((0, immutable_1.Map)()).withMutations((balancesByAccountId) => {
            const balMap = (0, balCache_1.getBalMap)();
            accounts.forEach((account) => {
                var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k, _l, _m, _o, _p, _q, _r, _s, _t, _u, _v, _w;
                if (account.type === 'INVESTMENT')
                    return;
                const excludePendingTxn = runningBalanceExcludeAccountIds === null || runningBalanceExcludeAccountIds === void 0 ? void 0 : runningBalanceExcludeAccountIds.includes(account.id);
                const isConnected = isConnectedAccount(account);
                const txns = transactionsByAccountId.get(account.id);
                let acctReminderSetting = 0;
                acctReminderSetting = accounts_1.accountsUtils.getReminderSettingInDays(account.recurringTxn);
                /*
                 * PER ACCOUNT CACHEING
                 */
                const cacheKey = account.id;
                const cacheRec = balMap[cacheKey];
                const cacheBroke = didBalanceCacheBreak(cacheRec, {
                    acctReminderSetting,
                    account,
                    txns,
                });
                if (!cacheBroke) {
                    balancesByAccountId.set(account.id, createNewBalancesRecord(balMap[cacheKey], { account, isConnected }));
                    // log.log('======================= CACHE HIT OR EMPTY TXNS FOR ', account.name);
                    return;
                }
                const cacheObject = {
                    accountId: account.id,
                    acctReminderSetting,
                    currentBalanceAsOf: account.currentBalanceAsOf,
                    currentBalanceAsOfOn: account.currentBalanceAsOfOn,
                    onlineBalance: account.onlineBalance,
                    balanceAsOf: account.balanceAsOf,
                    balanceAsOfOn: account.balanceAsOfOn,
                    cpOnlineBalance: (_c = (_b = (_a = account.aggregators) === null || _a === void 0 ? void 0 : _a.first) === null || _b === void 0 ? void 0 : _b.call(_a)) === null || _c === void 0 ? void 0 : _c.onlineBalance,
                    cpOnlineBalanceAt: (_f = (_e = (_d = account.aggregators) === null || _d === void 0 ? void 0 : _d.first) === null || _e === void 0 ? void 0 : _e.call(_d)) === null || _f === void 0 ? void 0 : _f.onlineBalanceAt,
                    cpAvailableBalance: (_j = (_h = (_g = account.aggregators) === null || _g === void 0 ? void 0 : _g.first) === null || _h === void 0 ? void 0 : _h.call(_g)) === null || _j === void 0 ? void 0 : _j.availableBalance,
                };
                const strictCacheObject = Object.assign(Object.assign({}, cacheObject), { txns });
                const ocmCacheName = `acctBalances-${account.id}`;
                const cachedValues = (0, optimalCacheManager_1.getOptimalCacheFromObject)(transactionsStore, ocmCacheName, cacheObject, strictCacheObject, (oldTxn, newTxn) => optimalCacheCompareFn(oldTxn, newTxn, account.id), (currentCache) => currentCache, 2);
                if (cachedValues) {
                    // log.log('OPTIMAL CACHE USED =========================================', account.id);
                    balancesByAccountId.set(account.id, cachedValues);
                    return;
                }
                /*
                 * FOR THIS ACCOUNT, the cache is broken or unavailable, so CALCULATIONS below
                 */
                atLeastOneAccountBrokeCache = true;
                let endingBalance = 0;
                let endingBalanceNoRecurring = 0;
                let currentBalance = account.currentBalanceAsOf;
                let txnsDeltaFromCurrentBalance = null;
                let earliestTransactionDate = null;
                let finalBalance = 0;
                let excludePendingTxnTotal = 0;
                let exlcudeC2RTxnTotal = 0;
                let setCache = false;
                // just use defaults until transactions are available
                if (txns && !transactionsAreLoading && !accountsAreLoading) {
                    setCache = true;
                    currentBalance = 0;
                    // if we have not already calculated the sum of transactions downloaded which match the accounts
                    // currentBalance values, and the account has a currentBalance, use it to do the calc
                    //
                    const calculateTxnDelta = account.currentBalanceAsOf && ((!cacheRec) ||
                        (cacheRec.txnsDeltaFromCurrentBalance === null) ||
                        (account.currentBalanceAsOf && account.currentBalanceAsOf !== cacheRec.account.currentBalanceAsOf) ||
                        (account.currentBalanceAsOfOn && account.currentBalanceAsOfOn !== cacheRec.account.currentBalanceAsOfOn));
                    log.log('======================= RECALCULATING FOR ', account.name, calculateTxnDelta, account.currentBalanceAsOf);
                    /*
                     * CALC METHOD 1 - 'R' Calculating the sum of original transactions associated with currentBalance
                     */
                    if (calculateTxnDelta || calculateTxnDelta === 0) {
                        earliestTransactionDate = txns.size ? txns.first().postedOn : luxon_1.DateTime.local().toFormat('yyyy-MM-dd');
                        endingBalance = account.currentBalanceAsOf;
                        endingBalanceNoRecurring = account.currentBalanceAsOf;
                        currentBalance = 0;
                        txnsDeltaFromCurrentBalance = endingBalance;
                        finalBalance = endingBalance;
                        txns.forEach((txn) => {
                            const txnDate = luxon_1.DateTime.fromISO(txn.postedOn);
                            const currentBalanceAsOfOnDate = luxon_1.DateTime.fromISO(account.currentBalanceAsOfOn);
                            if (!txnDate <= currentBalanceAsOfOnDate) {
                                finalBalance += Number(txn.amount);
                            }
                            if (includeTransactionInBalance(txn, {
                                recurringSetting: acctReminderSetting,
                                transactionsForAccountId: txns,
                                excludePendingTxn: undefined,
                                isPendingTxnDisabled: account.isBankPendingTxnsExcluded,
                            })) {
                                const earliestDate = luxon_1.DateTime.fromISO(earliestTransactionDate);
                                if (txnDate < earliestDate) {
                                    earliestTransactionDate = txn.postedOn;
                                }
                                if (!(0, utils_1.isUnacceptedScheduledTxn)(txn) && txnDate <= currentBalanceAsOfOnDate) {
                                    // @ts-expect-error: 'txnsDeltaFromCurrentBalance' is possibly 'null'.
                                    txnsDeltaFromCurrentBalance -= Number(txn.amount);
                                }
                                else {
                                    endingBalance += Number(txn.amount);
                                    endingBalanceNoRecurring += ((0, utils_1.isUnacceptedScheduledTxn)(txn) ? 0 : Number(txn.amount));
                                }
                                if (txnDate > luxon_1.DateTime.local().startOf('day')) {
                                    currentBalance += Number(txn.amount);
                                }
                                if (excludePendingTxn && (0, utils_1.isBankOwnedPending)(txn)) {
                                    excludePendingTxnTotal += Number(txn.amount);
                                }
                                if ((0, utils_1.isC2RTransaction)(txn)) {
                                    exlcudeC2RTxnTotal += Number(txn.amount);
                                }
                            }
                        });
                        endingBalance -= excludePendingTxnTotal + exlcudeC2RTxnTotal;
                        currentBalance = endingBalance - currentBalance;
                        assert(typeof currentBalance === 'number', 'currentBalance is not a number');
                    }
                    else {
                        /*
                         * CALC METHOD 2 and 3 - Calculating balance starting with either balanceAsOf or txnsDeltaFromCurrentBalance
                         */
                        // if we have a valid sum of original transactions in the cache, use it
                        const useTxnDeltaAmount = balMap[cacheKey] && balMap[cacheKey].txnsDeltaFromCurrentBalance !== null;
                        log.log('======================= CALC METHOD 2 FOR ', account.name, useTxnDeltaAmount, balMap[cacheKey] && balMap[cacheKey].txnsDeltaFromCurrentBalance);
                        earliestTransactionDate = txns.size ? txns.first().postedOn : luxon_1.DateTime.local().toFormat('yyyy-MM-dd');
                        endingBalance = useTxnDeltaAmount ? balMap[cacheKey].txnsDeltaFromCurrentBalance : Number(account.balanceAsOf);
                        txnsDeltaFromCurrentBalance = useTxnDeltaAmount ? balMap[cacheKey].txnsDeltaFromCurrentBalance : null;
                        currentBalance = endingBalance;
                        finalBalance = endingBalance;
                        endingBalanceNoRecurring = endingBalance;
                        txns.forEach((txn) => {
                            const txnDate = luxon_1.DateTime.fromISO(txn.postedOn);
                            const balanceAsOfOnDate = luxon_1.DateTime.fromISO(account.balanceAsOfOn);
                            if (useTxnDeltaAmount || (!account.balanceAsOfOn || txnDate > balanceAsOfOnDate)) {
                                finalBalance += Number(txn.amount);
                            }
                            if (includeTransactionInBalance(txn, {
                                recurringSetting: acctReminderSetting,
                                transactionsForAccountId: txns,
                                excludePendingTxn,
                                isPendingTxnDisabled: account.isBankPendingTxnsExcluded,
                            })) {
                                const earliestDate = luxon_1.DateTime.fromISO(earliestTransactionDate);
                                if (txnDate < earliestDate) {
                                    earliestTransactionDate = txn.postedOn;
                                }
                                // if we are using the 'R' value, date does not matter, but if we are calculating from balanceAsOfOn, we
                                // only count transactions that are after the balanceAsOfOn date
                                if (useTxnDeltaAmount || (!account.balanceAsOfOn || txnDate > balanceAsOfOnDate)) {
                                    endingBalance += Number(txn.amount);
                                    endingBalanceNoRecurring += ((0, utils_1.isUnacceptedScheduledTxn)(txn) ? 0 : Number(txn.amount));
                                    if (txnDate <= luxon_1.DateTime.local().startOf('day')) {
                                        currentBalance += Number(txn.amount);
                                    }
                                }
                            }
                        });
                    }
                }
                endingBalance = balRound(endingBalance);
                endingBalanceNoRecurring = balRound(endingBalanceNoRecurring);
                currentBalance = balRound(currentBalance);
                finalBalance = balRound(finalBalance);
                const balancesForAccountId = new types_2.AccountBalances({
                    accountId: account.id,
                    accountType: account.type,
                    onlineBalance: !isConnected ? currentBalance : account.onlineBalance,
                    endingBalance,
                    endingBalanceNoRecurring,
                    currentBalance,
                    finalBalance,
                    earliestTransactionDate,
                    txnsDeltaFromCurrentBalance,
                    cpOnlineBalance: (_m = (_l = (_k = account.aggregators) === null || _k === void 0 ? void 0 : _k.first) === null || _l === void 0 ? void 0 : _l.call(_k)) === null || _m === void 0 ? void 0 : _m.onlineBalance,
                    cpAvailableBalance: (_q = (_p = (_o = account.aggregators) === null || _o === void 0 ? void 0 : _o.first) === null || _p === void 0 ? void 0 : _p.call(_o)) === null || _q === void 0 ? void 0 : _q.availableBalance,
                });
                balancesByAccountId.set(account.id, balancesForAccountId);
                // Set Cache
                if (setCache) {
                    balMap[cacheKey] = {
                        endingBalance,
                        endingBalanceNoRecurring,
                        currentBalance,
                        finalBalance,
                        txns,
                        account,
                        earliestTransactionDate,
                        txnsDeltaFromCurrentBalance,
                        acctReminderSetting,
                        cpOnlineBalance: (_t = (_s = (_r = account.aggregators) === null || _r === void 0 ? void 0 : _r.first) === null || _s === void 0 ? void 0 : _s.call(_r)) === null || _t === void 0 ? void 0 : _t.onlineBalance,
                        cpAvailableBalance: (_w = (_v = (_u = account.aggregators) === null || _u === void 0 ? void 0 : _u.first) === null || _v === void 0 ? void 0 : _v.call(_u)) === null || _w === void 0 ? void 0 : _w.availableBalance,
                    };
                    // Set the optimal Cache
                    (0, optimalCacheManager_1.setOptimalCacheFromObject)(transactionsStore, ocmCacheName, cacheObject, strictCacheObject, balancesForAccountId, 2);
                }
            });
        });
        const balanceMapToReturn = atLeastOneAccountBrokeCache ? retVal : overallMapCache || retVal;
        overallMapCache = balanceMapToReturn;
        return balanceMapToReturn;
    });
})()));
// CMP-8174
// Running balance is balance today including due or overdue reminders if reminder prefrence is on for account
// For to calculate balace correctly, do the two steps
// case 1: for past dates, running balance is subtracted with amount of txns  and overdue schTxn instances between
// today and date for which balance is to be calculated
// case 2: for future dates, add amounts of due or future due schTxn and future txns till the date for
// which balance is to be calculated
const calcBalanceForAccountAtDate = (accountId, date, txnsByAccountId, balancesByAccountId, balanceStr, runningBalanceExcludeAccountIds) => {
    const balanceObject = balancesByAccountId.get(accountId);
    if (balanceObject) {
        let runningBalance = balanceObject === null || balanceObject === void 0 ? void 0 : balanceObject[balanceStr];
        const txns = txnsByAccountId.get(accountId);
        if (txns) {
            const today = luxon_1.DateTime.local().toISODate();
            const balMap = (0, balCache_1.getBalMap)();
            const cacheRec = balMap[accountId];
            const excludePendingTxn = runningBalanceExcludeAccountIds === null || runningBalanceExcludeAccountIds === void 0 ? void 0 : runningBalanceExcludeAccountIds.includes(accountId);
            const account = accounts_1.accountsSelectors.getAccountById(store_1.default === null || store_1.default === void 0 ? void 0 : store_1.default.getState(), accountId);
            const parsedDate = luxon_1.DateTime.fromISO(date);
            const currentDate = luxon_1.DateTime.fromISO(today);
            if (!parsedDate.hasSame(currentDate, 'day')) {
                txns.forEach((txn) => {
                    if (!includeTransactionInBalance(txn, {
                        recurringSetting: cacheRec === null || cacheRec === void 0 ? void 0 : cacheRec.acctReminderSetting,
                        transactionsForAccountId: txnsByAccountId,
                        excludePendingTxn,
                        isPendingTxnDisabled: account === null || account === void 0 ? void 0 : account.isBankPendingTxnsExcluded,
                    })) {
                        return;
                    }
                    const txnPostedDate = luxon_1.DateTime.fromISO(txn.postedOn).toFormat('yyyy-MM-dd');
                    const txnPostedDateLuxon = luxon_1.DateTime.fromISO(txnPostedDate);
                    if (parsedDate > currentDate) {
                        if (txnPostedDateLuxon > currentDate && txnPostedDateLuxon <= parsedDate) {
                            runningBalance += Number(txn.amount);
                        }
                    }
                    else if (txnPostedDateLuxon <= currentDate && txnPostedDateLuxon > parsedDate) {
                        runningBalance -= Number(txn.amount);
                    }
                });
            }
        }
        return runningBalance === null || runningBalance === void 0 ? void 0 : runningBalance.toFixed(2);
    }
    return null;
};
exports.getBalanceForAccountAtDate = (0, reselect_1.createSelector)((state, props) => props.accountId, (state, props) => props.date, (state, props) => props.balanceType || 'endingBalance', selectors_4.getTransactionsByAccountId, exports.getBalancesByAccountId, selectors_3.getRunningBalanceExcludeAccountIds, (accountId, date, balanceType, txnsByAccountId, balancesByAccountId, runningBalanceExcludeAccountIds) => calcBalanceForAccountAtDate(accountId, date, txnsByAccountId, balancesByAccountId, balanceType, runningBalanceExcludeAccountIds));
exports.getBalancesForAccountsAtDates = (0, reselect_1.createSelector)((state, props) => props.accountIds, (state, props) => props.dates, selectors_4.getTransactionsByAccountId, exports.getBalancesByAccountId, accounts_1.accountsSelectors.getAccountsById, selectors_3.getRunningBalanceExcludeAccountIds, (accountIds, dates, txnsByAccountId, balancesByAccountId, accountsById, runningBalanceExcludeAccountIds) => {
    const currBalanceStr = accountIds.map((id) => { var _a; return (_a = balancesByAccountId.get(id)) === null || _a === void 0 ? void 0 : _a.currentBalance; });
    const endingBalanceStr = accountIds.map((id) => { var _a; return (_a = balancesByAccountId.get(id)) === null || _a === void 0 ? void 0 : _a.endingBalance; });
    const cacheStr = accountIds &&
        dates &&
        `${accountIds.toJS().toString()}-${currBalanceStr.toJS().toString()}-${endingBalanceStr.toJS().toString()}-${dates[0]}-${dates[dates.length - 1]}`;
    return (0, memoizeWithKey_1.memoizeWithKey)(cacheStr, () => dates.reduce((dateBalances, date) => {
        let balanceTotal = 0;
        const accountsBalances = accountIds.reduce((acc, accountId) => {
            const account = accountsById.get(accountId);
            if (!account)
                return acc;
            if (account.type === 'INVESTMENT')
                return acc; // exclude investment accounts
            const balance = calcBalanceForAccountAtDate(accountId, date, txnsByAccountId, balancesByAccountId, 'currentBalance', runningBalanceExcludeAccountIds);
            balanceTotal = Number(parseFloat(balanceTotal) + parseFloat(balance !== null && balance !== void 0 ? balance : 0));
            return acc.set(accountId, { name: account.name, balance });
        }, (0, immutable_1.OrderedMap)());
        return dateBalances.set(date, { accountsBalances, total: balanceTotal });
    }, (0, immutable_1.OrderedMap)()));
});
const investmentsBalMap = {};
const getInvestmentBalancesByAccountIdQuicken = (0, reselect_1.createSelector)(accounts_1.accountsSelectors.getAccountsById, selectors_2.getIHoldsByAccountId, (accounts, iHoldsByAccountId) => (((0, immutable_1.Map)()).withMutations((balancesByAccountId) => {
    accounts.forEach((account) => {
        if (account.type !== 'INVESTMENT')
            return;
        const holdings = iHoldsByAccountId === null || iHoldsByAccountId === void 0 ? void 0 : iHoldsByAccountId.get(account.id);
        // Try and use the cache. Holdings are just a one shot load unlike the other accounts so this doesn't get used.
        if (holdings && (investmentsBalMap[account.id] && investmentsBalMap[account.id].holdings === holdings)) {
            const endBal = investmentsBalMap[account.id] ? investmentsBalMap[account.id].endingBalance : 0;
            const currBal = investmentsBalMap[account.id] ? investmentsBalMap[account.id].currentBalance : 0;
            const eDate = investmentsBalMap[account.id] ? investmentsBalMap[account.id].earliestTransactionDate : null;
            balancesByAccountId.set(account.id, new types_2.AccountBalances({
                accountId: account.id,
                accountType: account.type,
                endingBalance: endBal,
                onlineBalance: endBal,
                currentBalance: currBal,
                earliestTransactionDate: eDate,
            }));
            // log.log('======================= CACHE HIT OR EMPTY HOLDINGS FOR ', account.name);
            return;
        }
        // log.log('======================= RECALCULATING HOLDING BALANCES FOR ', account.name);
        /*
          Loop over the investment holdings for this account and determine:
            1. The balanceAsOfOn from the latest modifiedAt date from each.
            2. The earliestTransactionDate from the earliest modifiedAt date from each.
            3. The endingBalance by summing latestPrice * numberOfShares + the account's cash balance
            4. The currentBalance = endingBalance
         */
        let balanceAsOfOn;
        let earliestTransactionDate = luxon_1.DateTime.local();
        let endingBalance = 0.0;
        if (holdings) {
            holdings.forEach((holding) => {
                const modifiedAtDate = luxon_1.DateTime.fromISO(holding.modifiedAt).toFormat('MM-dd-yyyy');
                if (!balanceAsOfOn)
                    balanceAsOfOn = modifiedAtDate;
                if (!earliestTransactionDate)
                    earliestTransactionDate = modifiedAtDate;
                if (modifiedAtDate > new Date(balanceAsOfOn)) {
                    balanceAsOfOn = modifiedAtDate;
                }
                // @ts-expect-error: Operator '<' cannot be applied to types 'string' and 'Date'.
                if (modifiedAtDate < new Date(earliestTransactionDate)) {
                    earliestTransactionDate = modifiedAtDate;
                }
                endingBalance += (holding.numberOfShares * holding.latestPrice);
            });
        }
        // @ts-expect-error: Property 'cashBalance' does not exist on type 'RecordOf<AccountProps>'.
        endingBalance += account.cashBalance;
        const balancesForAccountId = new types_2.AccountBalances({
            accountId: account.id,
            accountType: account.type,
            currentBalance: endingBalance,
            onlineBalance: endingBalance,
            endingBalance,
            earliestTransactionDate,
        });
        balancesByAccountId.set(account.id, balancesForAccountId);
        investmentsBalMap[account.id] = {
            endingBalance,
            currentBalance: endingBalance,
            holdings,
            earliestTransactionDate,
        };
    });
})));
exports.getAllBalancesByAccountId = (0, reselect_1.createSelector)(exports.getBalancesByAccountId, getInvestmentBalancesByAccountIdQuicken, (balancesByAccountId, investmentBalancesByAccountId) => balancesByAccountId.merge(investmentBalancesByAccountId));
// add defaultBalance to the balances
exports.getAllBalancesByAccountIdEx = (0, reselect_1.createSelector)(exports.getAllBalancesByAccountId, (state) => (0, selectors_1.getSharedPreferencesByPath)(state, { group: 'dataset', path: ['shared', 'showBalance'] }) || types_1.ShowBalance.INCLUDE_PENDING, (state) => feature_flags_1.featureFlagsSelectors.getFeatureFlag(state, 'balanceToggle'), (balancesByAccountId, showBalance, balanceToggle) => balancesByAccountId.map((balances) => {
    let defaultBalance = balances.currentBalance;
    if (balanceToggle) {
        switch (showBalance) {
            case types_1.ShowBalance.INCLUDE_PENDING:
                defaultBalance = balances.currentBalance;
                break;
            case types_1.ShowBalance.BANK_REPORTED:
                defaultBalance = balances.cpOnlineBalance;
                break;
            default:
                verify(false, `unexpected showBalance value = ${showBalance}`);
        }
    }
    return balances.set('defaultBalance', defaultBalance);
}));
